import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
    useAdoptTool,
    useAdoptedTools,
    useRemoveTool,
    useTools,
    groupToolsForDock,
    isPaidTool,
} from "../../marketplace";
import type { TherapyTool } from "../../marketplace";
import { useCompleteOnboarding } from "../../therapist";
import {
    CATALOG_PATH,
    DEFAULT_SLUGS,
    PICKER_STEP,
    PUBLIC_PROFILE_STEP,
    isAlreadyAdopted,
    type Step,
} from "../domain/steps";

/** One heading of the picker, with the tools filed under it. */
export type PickerGroup = {
    key: string;
    labelKey: string;
    tools: TherapyTool[];
};

export type OnboardingFlow = {
    isOpen: boolean;
    step: Step | null;
    stepNumber: number;
    stepCount: number;
    isFinalScreen: boolean;
    /** True while the adoptions are in flight; controls are locked. */
    isBusy: boolean;
    /** Set when an adoption failed and can be retried. */
    hasFailed: boolean;
    /** The catalogue could not be read, so there is nothing to offer. */
    catalogUnavailable: boolean;
    /** What the picker shows, grouped the way the dock groups it. */
    groups: PickerGroup[];
    /** Ids of the tools ticked right now. */
    selected: ReadonlySet<string>;
    /** Ticked and fixed: paid for already, so there is nothing to decide. */
    locked: ReadonlySet<string>;
    /** Names of the tools whose adoption failed, for the message. */
    failedTools: TherapyTool[];
    toggle: (toolId: string) => void;
    accept: () => void;
    decline: () => void;
    previous: () => void;
    close: () => void;
};

/**
 * The onboarding flow's rules, kept out of the component that draws them.
 *
 * One screen of tools, then the public-profile offer, then a confirmation.
 * The tools already adopted are left out of the picker rather than shown
 * ticked: a therapist who becomes Plus after using Alchely for a month should
 * be asked about what is new to them, not about what they already have.
 */
export const useOnboardingFlow = (): OnboardingFlow => {
    const { data: tools, isError: toolsFailed } = useTools({});
    const { data: adoptedTools, isLoading: loadingAdopted } = useAdoptedTools();
    const { mutate: adopt } = useAdoptTool();
    const { mutate: remove } = useRemoveTool();
    const { completeOnboarding } = useCompleteOnboarding();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(true);
    const [steps, setSteps] = useState<Step[] | null>(null);
    const [index, setIndex] = useState(0);
    const [failedIds, setFailedIds] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState<Set<string> | null>(null);

    const indexRef = useRef(0);
    useEffect(() => {
        indexRef.current = index;
    }, [index]);

    /**
     * What is left to offer, frozen once: adopting mid-flow must not make the
     * screen rewrite itself under the therapist's hands.
     */
    const [offered, setOffered] = useState<TherapyTool[] | null>(null);
    /** The tools already adopted when the screen opened, by id. */
    const [held, setHeld] = useState<ReadonlySet<string>>(new Set<string>());
    useEffect(() => {
        // Nothing to offer and no way to find out: end rather than hold a
        // therapist on a screen with no tools on it.
        if (toolsFailed && steps === null) {
            completeOnboarding();
            setIsOpen(false);
            return;
        }
        if (offered !== null || loadingAdopted || tools === undefined) return;
        const adoptedSlugs = new Set((adoptedTools ?? []).map((tool) => tool.slug));
        // A tool with a price of its own is never **offered** here: it is
        // bought where its price is shown — the subscription screen, or the
        // catalogue with its confirmation — and a setup screen that says
        // nothing about money must not be able to add one to somebody's bill.
        //
        // One that has already been paid for is a different thing, and it is
        // shown: somebody who bought AlchelyMeeting with their plan should
        // meet it while setting up, not wonder where what they paid for went.
        // It arrives held, and `lockedIds` below keeps it that way.
        //
        // A tool that comes with another — Genogram & Ecomap, with
        // Constellation — follows the same rule as the one it comes with:
        // shown when that one is held, never offered alone.
        setOffered(
            tools.filter((tool) =>
                isPaidTool(tool) || tool.included_with
                    ? adoptedSlugs.has(tool.slug)
                    : true
            )
        );
        // What they hold as they walk in. The difference between this and
        // what is ticked when they press the button is what gets done.
        setHeld(new Set((adoptedTools ?? []).map((tool) => tool.id)));
    }, [offered, loadingAdopted, adoptedTools, tools, toolsFailed, steps, completeOnboarding]);

    useEffect(() => {
        if (steps !== null || offered === null) return;

        // Nothing new to offer — an established therapist who already has
        // every tool — so there is no setup to walk through.
        if (offered.length === 0) {
            completeOnboarding();
            setIsOpen(false);
            return;
        }

        // Ticked: everything they already hold, plus the recommended ones
        // they do not. A tool they turned off last month opens turned off.
        setSelected(
            new Set(
                offered
                    .filter(
                        (tool) =>
                            held.has(tool.id) ||
                            isPaidTool(tool) ||
                            DEFAULT_SLUGS.includes(tool.slug)
                    )
                    .map((tool) => tool.id)
            )
        );
        setSteps([PICKER_STEP, PUBLIC_PROFILE_STEP]);
    }, [steps, offered, held, completeOnboarding]);

    const step = steps && index < steps.length ? steps[index] : null;
    const isFinalScreen = steps !== null && index >= steps.length;

    /**
     * The offered tools under the headings the dock uses.
     *
     * Reused rather than reinvented so a therapist meets the same grouping
     * here and on their home screen: administration first, then the
     * instruments, gathered by the way of working they belong to.
     */
    const groups = useMemo<PickerGroup[]>(() => {
        if (!offered) return [];
        return groupToolsForDock(offered).map((group) => ({
            key: group.key,
            labelKey: group.labelKey,
            tools: group.tools,
        }));
    }, [offered]);

    /**
     * The tools on this screen that are already theirs and paid for.
     *
     * Ticked, and neither untickable nor re-adopted: unticking would promise
     * to remove something bought minutes ago, and adopting it again would ask
     * the API for what it already has.
     */
    /** Every tool on this screen, for telling a removal here from one elsewhere. */
    const offeredIds = useMemo(
        () => new Set((offered ?? []).map((tool) => tool.id)),
        [offered]
    );

    const lockedIds = useMemo(
        () =>
            new Set(
                (offered ?? [])
                    .filter((tool) => isPaidTool(tool) || tool.included_with)
                    .map((tool) => tool.id)
            ),
        [offered]
    );

    const toggle = useCallback(
        (toolId: string) => {
            if (lockedIds.has(toolId)) return;
            setSelected((current) => {
                const next = new Set(current ?? []);
                if (next.has(toolId)) next.delete(toolId);
                else next.add(toolId);
                return next;
            });
        },
        [lockedIds]
    );

    const advance = useCallback(() => {
        setFailedIds([]);
        setIndex((current) => current + 1);
    }, []);

    /**
     * Adopts the chosen tools one after another, collecting what failed.
     *
     * Sequential rather than parallel so a half-failed screen leaves one
     * retryable message rather than several racing ones, and it keeps going
     * after a failure: a therapist who ticked five tools should end up with
     * the four that worked, not with whichever came before the first error.
     */
    const adoptAll = useCallback(
        (ids: string[], done: (failed: string[]) => void) => {
            const failed: string[] = [];
            const next = (remaining: string[]) => {
                const [id, ...rest] = remaining;
                if (id === undefined) {
                    done(failed);
                    return;
                }
                adopt(id, {
                    onSuccess: () => next(rest),
                    onError: (error: unknown) => {
                        if (!isAlreadyAdopted(error)) failed.push(id);
                        next(rest);
                    },
                });
            };
            next(ids);
        },
        [adopt]
    );

    /**
     * Drops the tools that were turned off, one after another.
     *
     * The other half of a switch. Without it the picker could only ever add,
     * so a therapist who unticked something watched it come back on the next
     * screen — and the only way out was the catalogue they had not seen yet.
     *
     * A failure here is not worth a retry screen: nothing was charged and
     * nothing was lost, the tool is simply still there, and the catalogue
     * removes it in one press. Adoption failures are what the message is for.
     */
    const removeAll = useCallback(
        (ids: string[], done: () => void) => {
            const next = (remaining: string[]) => {
                const [id, ...rest] = remaining;
                if (id === undefined) {
                    done();
                    return;
                }
                remove(id, { onSuccess: () => next(rest), onError: () => next(rest) });
            };
            next(ids);
        },
        [remove]
    );

    const close = useCallback(() => {
        completeOnboarding();
        setIsOpen(false);
    }, [completeOnboarding]);

    /** Ends the onboarding and hands the therapist to the page that finishes
     *  the job — nothing is published by having answered yes here. */
    const goTo = useCallback(
        (path: string) => {
            completeOnboarding();
            setIsOpen(false);
            navigate(path);
        },
        [completeOnboarding, navigate]
    );

    const accept = useCallback(() => {
        if (!step) return;
        if (step.kind === "navigate") {
            goTo(step.path);
            return;
        }

        // What changed, not what is ticked: a switch left where it was is
        // nothing to do, and asking the API for what it already has is how a
        // setup screen turns into a queue of 409s.
        //
        // Retrying asks for the failed ones again; adopting twice answers 409
        // either way, which this treats as the outcome it wanted.
        const ticked = new Set([...(selected ?? [])].filter((id) => !lockedIds.has(id)));
        const toAdopt = [...ticked].filter((id) => !held.has(id));
        const toRemove = [...held].filter(
            (id) => !ticked.has(id) && !lockedIds.has(id) && offeredIds.has(id)
        );

        if (toAdopt.length === 0 && toRemove.length === 0) {
            advance();
            return;
        }

        const answeredIndex = indexRef.current;
        setBusy(true);
        setFailedIds([]);
        removeAll(toRemove, () => {
            adoptAll(toAdopt, (failed) => {
                setBusy(false);
                if (indexRef.current !== answeredIndex) return;
                // Whatever went through is theirs now, so a retry asks only
                // for the rest.
                setHeld(
                    new Set(
                        [...ticked].filter((id) => !failed.includes(id))
                    )
                );
                if (failed.length > 0) {
                    setFailedIds(failed);
                    return;
                }
                advance();
            });
        });
    }, [step, selected, held, lockedIds, offeredIds, adoptAll, removeAll, advance, goTo]);

    const previous = useCallback(() => {
        setFailedIds([]);
        setIndex((current) => Math.max(0, current - 1));
    }, []);

    const failedTools = useMemo(
        () => (offered ?? []).filter((tool) => failedIds.includes(tool.id)),
        [offered, failedIds]
    );

    return {
        isOpen,
        step,
        stepNumber: index + 1,
        stepCount: steps?.length ?? 0,
        isFinalScreen,
        isBusy: busy,
        hasFailed: failedIds.length > 0,
        catalogUnavailable: toolsFailed || offered?.length === 0,
        groups,
        selected: selected ?? new Set<string>(),
        locked: lockedIds,
        failedTools,
        toggle,
        accept,
        decline: advance,
        previous,
        close,
    };
};

export { CATALOG_PATH };
